<template>
  <div id="app">
    <LandingPage v-if="showLandingPage" @enter-app="showLandingPage = false" />

    <template v-else>
      <div v-if="!$route?.meta?.hideTopControls" class="top-controls">
        <router-link
          v-if="!$route?.meta?.hideDashboardButton"
          to="/dashboard"
          class="dash-btn"
        >
          <span class="sidebar-nav-icon" aria-hidden="true">⌂</span>
          Dashboard
        </router-link>

        <div v-if="!$route?.meta?.hideLocationSelector" class="location-selector">
          <label for="location">Location</label>
          <select id="location" v-model="selectedLocation" @change="saveLocation">
            <option value="F">Fresno</option>
            <option value="D">Denver</option>
            <option value="K">Knoxville</option>
            <option value="L">Lakeland</option>
          </select>
        </div>

        <button class="logout-btn" @click="logout">Logout</button>
      </div>

      <router-view @locationChanged="updateLocation" />
    </template>
  </div>
</template>

<script>
import LandingPage from './components/LandingPage.vue';
import axios from 'axios';

export default {
  components: { LandingPage },
  data() {
    return {
      showLandingPage: false,
      selectedLocation: localStorage.getItem('selectedLocation') || 'D',
    };
  },
  methods: {
    updateLocation(newLocation) {
      this.selectedLocation = newLocation;
      localStorage.setItem('selectedLocation', newLocation);
    },
    saveLocation() {
      localStorage.setItem('selectedLocation', this.selectedLocation);
    },
    async logout() {
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          await axios.post('/auth/logout', { refresh_token: refreshToken });
        }
      } catch (e) {
        console.warn('Logout request failed:', e);
      } finally {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('selectedLocation');
        this.$router.replace('/');
      }
    },
  },
};
</script>

<style>
/* Shared design tokens.  Only consumed inside the scheduling screen. */
#app:has(.jim-scheduling-container) {
  --pablo-bg: #0b1421;
  --pablo-sidebar: #0d1b2c;
  --pablo-panel: #142338;
  --pablo-surface: #1b2d42;
  --pablo-field: #21374e;
  --pablo-border: #30465e;
  --pablo-text: #f0f6ff;
  --pablo-muted: #9eb2c9;
  --pablo-blue: #1765e9;
  --pablo-green: #13bb83;
  --pablo-red: #e24444;
  --pablo-amber: #f7c23e;
  background: var(--pablo-bg);
  color: var(--pablo-text);
  min-height: 100vh;
  font-family: Inter, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

/* Restrict the new navigation shell to the scheduling screen. */
#app:has(.jim-scheduling-container)::before {
  content: '';
  position: fixed;
  inset: 0 auto 0 0;
  width: 238px;
  z-index: 30;
  background: linear-gradient(180deg, #102139, #0b1928);
  border-right: 1px solid #253b52;
  pointer-events: none;
}

#app:has(.jim-scheduling-container) .top-controls {
  position: fixed;
  top: 14px;
  left: 14px;
  width: 210px;
  z-index: 70;
  display: flex;
  flex-direction: column;
  gap: 17px;
  align-items: stretch;
  font-size: 13px;
}

#app:has(.jim-scheduling-container) .dash-btn {
  margin: 0;
  min-height: 48px;
  padding: 0 14px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 11px;
  border: 1px solid var(--pablo-border);
  border-radius: 11px;
  background: #1b2b42;
  color: var(--pablo-text);
  text-decoration: none;
  font: 600 14px/1 Inter, 'Segoe UI', sans-serif;
}

#app:has(.jim-scheduling-container) .sidebar-nav-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 25px;
  width: 25px;
  color: #c4d9fb;
}

#app:has(.jim-scheduling-container) .dash-btn:hover,
#app:has(.jim-scheduling-container) .logout-btn:hover {
  background: #29425e;
}

#app:has(.jim-scheduling-container) .location-selector {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 3px;
  color: #bcd0e7;
  background: transparent;
  border: 0;
  box-shadow: none;
}

#app:has(.jim-scheduling-container) .location-selector label {
  font-weight: 650;
}

#app:has(.jim-scheduling-container) .location-selector select {
  width: 100%;
  height: 42px;
  margin: 0;
  padding: 0 12px;
  color: var(--pablo-text);
  font: inherit;
  font-weight: 650;
  background: #1b2d44;
  border: 1px solid #344c66;
  border-radius: 9px;
  cursor: pointer;
}

#app:has(.jim-scheduling-container) .logout-btn {
  position: fixed;
  bottom: 16px;
  left: 15px;
  width: 208px;
  height: 40px;
  margin: 0;
  border: 1px solid #36506a;
  border-radius: 9px;
  background: #192d43;
  color: #dce9f7;
  font: 600 13px Inter, 'Segoe UI', sans-serif;
  cursor: pointer;
}

@media (max-width: 900px) {
  #app:has(.jim-scheduling-container)::before { width: 196px; }
  #app:has(.jim-scheduling-container) .top-controls { width: 170px; }
  #app:has(.jim-scheduling-container) .logout-btn { width: 168px; }
}
</style>

<style scoped>
#app {
  min-height: 100vh;
  color: #f9fafb;
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.top-controls {
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 999999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}
.dash-btn {
  display: inline-block;
  text-decoration: none;
  background-color: #111827;
  color: #e5e7eb;
  border: 1px solid rgba(203, 191, 191, 0.25);
  border-radius: 10px;
  margin-top: 20px;
  padding: 8px 12px;
  font-weight: 700;
  font-size: 14px;
}
.dash-btn:hover { background: #1f2933; }
.location-selector {
  background: #5595b7c2;
  padding: 8px 10px;
  border-radius: 6px;
  font-weight: bold;
  color: #0b0101;
}
.location-selector select { margin-left: 6px; }
.logout-btn {
  background-color: #7affe276;
  color: #0b0101;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 8px;
  padding: 8px 12px;
  font-weight: 800;
  font-size: 13px;
  cursor: pointer;
}
.logout-btn:hover { background-color: #5595b7c2; }
</style>
